import { generateCorrelationId } from '@/shared/correlation';

/** Options passed alongside every API call. */
export type ApiOpts = {
  correlationId?: string;
  signal?: AbortSignal;
};

/** Structured error returned by the TRMS backend. */
export type ApiErrorBody = {
  error: string;
  message?: string;
  correlation_id?: string;
  timestamp?: string;
  layer?: string;
  details?: Array<{ path: string; code?: string; message: string }>;
  // Populated on 409 duplicate-identifier responses from POST /api/v1/instruments.
  identifier_kind?: string;
  identifier_value?: string;
  existing_instrument_id?: string;
};

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message ?? body.error);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export class DuplicateInstrumentError extends ApiError {
  readonly identifierKind: string;
  readonly identifierValue: string;
  readonly existingInstrumentId: string;

  constructor(
    status: number,
    body: ApiErrorBody,
    identifierKind: string,
    identifierValue: string,
    existingInstrumentId: string,
  ) {
    super(status, body);
    this.name = 'DuplicateInstrumentError';
    this.identifierKind = identifierKind;
    this.identifierValue = identifierValue;
    this.existingInstrumentId = existingInstrumentId;
  }
}

export class ValuationFailedError extends ApiError {
  constructor(status: number, body: ApiErrorBody) {
    super(status, body);
    this.name = 'ValuationFailedError';
  }
}

export class FxRateUnavailableError extends ValuationFailedError {
  readonly pair?: string;
  readonly asOfDate?: string;

  constructor(status: number, body: ApiErrorBody, pair?: string, asOfDate?: string) {
    super(status, body);
    this.name = 'FxRateUnavailableError';
    this.pair = pair;
    this.asOfDate = asOfDate;
  }
}

// Token accessor injected at runtime by the auth layer.
// Avoids a circular dependency between client.ts and AuthProvider.
let _getToken: (() => string | null) | null = null;

export function configureApiClient(getToken: () => string | null): void {
  _getToken = getToken;
}

/**
 * Returns the current auth token via the accessor injected by the auth layer,
 * or `null` if none is configured. Use this instead of reading storage directly
 * so callers that bypass {@link apiClient} (e.g. blob downloads) stay in sync
 * with how the rest of the client authenticates.
 */
export function getAuthToken(): string | null {
  return _getToken?.() ?? null;
}

/**
 * Resolves a request path against {@link import.meta.env.VITE_API_BASE_URL}.
 * Exported so callers that issue a raw `fetch` (e.g. blob downloads) target the
 * same origin as {@link apiClient}.
 */
export function resolveApiUrl(path: string): string {
  if (/^https?:\/\//.test(path)) {
    return path;
  }

  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (typeof baseUrl === 'string' && baseUrl.trim() !== '') {
    const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${normalizedBase}${normalizedPath}`;
  }

  return path;
}

function parseFxRateUnavailableContext(message: string): { pair?: string; asOfDate?: string } | null {
  const directPair = /^FX forward curve data is not configured for pair (\S+) tenor \S+ on (\d{4}-\d{2}-\d{2})$/u.exec(message);
  if (directPair) {
    return { pair: directPair[1], asOfDate: directPair[2] };
  }

  const baseLabel = /^FX (?:grid point not found|rate not found|rate value is null|rate must be positive): index=FX_SPOT\.([A-Z]{3}) label=([A-Z]{3})(?: asOf=(\d{4}-\d{2}-\d{2}))?/u.exec(message);
  if (baseLabel) {
    return { pair: `${baseLabel[1]}/${baseLabel[2]}`, asOfDate: baseLabel[3] };
  }

  const baseOnly = /^FX index not found: FX_SPOT\.([A-Z]{3})$/u.exec(message);
  if (baseOnly) {
    return { pair: `${baseOnly[1]}/?` };
  }

  return null;
}

function toApiError(status: number, body: ApiErrorBody): ApiError {
  if (
    status === 409 &&
    typeof body.existing_instrument_id === 'string' &&
    typeof body.identifier_kind === 'string' &&
    typeof body.identifier_value === 'string'
  ) {
    return new DuplicateInstrumentError(
      status,
      body,
      body.identifier_kind,
      body.identifier_value,
      body.existing_instrument_id,
    );
  }

  if (body.error === 'valuation_failed') {
    const context = typeof body.message === 'string' ? parseFxRateUnavailableContext(body.message) : null;
    if (context) {
      return new FxRateUnavailableError(status, body, context.pair, context.asOfDate);
    }
    return new ValuationFailedError(status, body);
  }

  return new ApiError(status, body);
}

async function request<T>(
  method: string,
  path: string,
  body: unknown,
  opts?: ApiOpts,
): Promise<T> {
  const token = _getToken?.();
  const correlationId = opts?.correlationId ?? generateCorrelationId();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Correlation-ID': correlationId,
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = resolveApiUrl(path);
  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal: opts?.signal,
  });

  if (!res.ok) {
    let errorBody: ApiErrorBody;
    try {
      errorBody = (await res.json()) as ApiErrorBody;
    } catch {
      errorBody = { error: res.statusText };
    }
    throw toApiError(res.status, errorBody);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const responseText = await res.text();
  if (responseText.trim().length === 0) {
    return undefined as T;
  }

  return JSON.parse(responseText) as T;
}

export const apiClient = {
  get: <T>(path: string, opts?: ApiOpts): Promise<T> =>
    request<T>('GET', path, undefined, opts),

  post: <T>(path: string, body: unknown, opts?: ApiOpts): Promise<T> =>
    request<T>('POST', path, body, opts),

  put: <T>(path: string, body: unknown, opts?: ApiOpts): Promise<T> =>
    request<T>('PUT', path, body, opts),

  patch: <T>(path: string, body: unknown, opts?: ApiOpts): Promise<T> =>
    request<T>('PATCH', path, body, opts),

  delete: <T>(path: string, opts?: ApiOpts): Promise<T> =>
    request<T>('DELETE', path, undefined, opts),
};
